import { useEffect, useMemo, useState } from 'react';
import type { AuthUser } from './storage';

const AVATAR_PREFIX = 'apc.avatar.';
const AVATAR_EVENT = 'apc-avatar-change';
const MAX_SOURCE_SIZE = 10 * 1024 * 1024;
const MAX_AVATAR_SIZE = 512;

function getAvatarKey(user: AuthUser | null | undefined) {
    const identity = String(user?.id || user?.email || '').trim().toLowerCase();
    return identity ? `${AVATAR_PREFIX}${encodeURIComponent(identity)}` : '';
}

export function getStoredAvatar(user: AuthUser | null | undefined) {
    const key = getAvatarKey(user);
    if (!key) return '';

    try {
        return localStorage.getItem(key) || '';
    } catch {
        return '';
    }
}

export function saveStoredAvatar(user: AuthUser, dataUrl: string) {
    const key = getAvatarKey(user);
    if (!key) throw new Error('Unable to identify the current account.');

    localStorage.setItem(key, dataUrl);
    window.dispatchEvent(new CustomEvent(AVATAR_EVENT, { detail: key }));
}

export function useStoredAvatar(user: AuthUser | null | undefined) {
    const key = useMemo(() => getAvatarKey(user), [user?.email, user?.id]);
    const [avatar, setAvatar] = useState(() => getStoredAvatar(user));

    useEffect(() => {
        setAvatar(getStoredAvatar(user));

        const updateAvatar = (event: Event) => {
            if (event instanceof StorageEvent && event.key !== key) return;
            if (event instanceof CustomEvent && event.detail !== key) return;
            setAvatar(getStoredAvatar(user));
        };

        window.addEventListener('storage', updateAvatar);
        window.addEventListener(AVATAR_EVENT, updateAvatar);
        return () => {
            window.removeEventListener('storage', updateAvatar);
            window.removeEventListener(AVATAR_EVENT, updateAvatar);
        };
    }, [key, user]);

    return avatar;
}

function loadImage(file: File) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Unable to read this image.'));
        };
        image.src = url;
    });
}

export async function compressAvatar(file: File) {
    if (!file.type.startsWith('image/')) {
        throw new Error('Please choose an image file.');
    }
    if (file.size > MAX_SOURCE_SIZE) {
        throw new Error('The source image must be smaller than 10 MB.');
    }

    const image = await loadImage(file);
    const scale = Math.min(1, MAX_AVATAR_SIZE / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('Unable to process this image.');

    context.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL('image/webp', 0.8);
}
